import { useEffect, useMemo } from "react";
import { Alert, Box, Button, CircularProgress, Container, Paper, Stack, TableBody, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CeldaKit, CeldaPrincipal, Columna, FilaKit, TablaKit } from "../../components/kit/TablaKit";
import EstadoChip from "../../components/kit/EstadoChip";
import Etiqueta from "../../components/kit/Etiqueta";
import FiltrosTemas, { DISPONIBILIDAD, OpcionFiltro } from "../../components/tema/FiltrosTemas";
import { Tarjeta, TituloTarjeta } from "../../components/kit/Tarjeta";
import { useAreas, useTemasInfinito } from "../../queries/useTemas";
import { GuiaOpcion, Tema } from "../../types/tema";
import { portalLoginUrl } from "../../utils/portal";
import { GestoDeApertura, useIr } from "../../utils/enlaces";
import { categoriaDe } from "../../utils/categoria";
import { useFiltrosUrl } from "../../utils/estadoLista";
import { tonoDeTema } from "../../utils/estados";
import { Accesores, ordenar, useOrden } from "../../utils/orden";
import { useFinALaVista } from "../../utils/scrollInfinito";

/** Temas por tanda. El catálogo tiene cientos: se muestra una tanda y el scroll
 *  va pidiendo las siguientes. */
const TANDA = 24;

// Título y categoría mandan el ancho (40 % y 20 %). Del resto, el estado ocupa lo
// justo para su chip y las guías se quedan con lo que queda, que es texto largo.
const COLUMNAS: (string | Columna)[] = [
  { label: "Título", sortKey: "titulo", width: "40%" },
  { label: "Categoría", sortKey: "categoria", width: "20%" },
  { label: "Estado", sortKey: "estado", width: 140 },
  { label: "Guías", sortKey: "guia" },
];

const ACC_TEMA: Accesores<Tema> = {
  titulo: (t) => t.titulo,
  categoria: (t) => categoriaDe(t),
  estado: (t) => t.estado,
  guia: (t) => t.guia,
};

// Fuera solo hay disponibles y asignados: ofrecer "No disponible" o "En pausa"
// sería un filtro vacío (el catálogo público no los muestra).
const OCULTOS_AFUERA = ["revision", "pausado"];
const DISPONIBILIDAD_PUBLICA = DISPONIBILIDAD.filter((d) => !OCULTOS_AFUERA.includes(d.value));

/**
 * Página pública "Temas": el catálogo (disponibles y asignados) con la misma
 * barra de filtros que el catálogo de la app —categoría, disponibilidad y guía—,
 * para que buscar un tema se haga igual dentro y fuera de la sesión.
 *
 * El catálogo se recorre con scroll infinito. Los filtros y cuántas tandas se
 * llevan cargadas viven en la URL, así que volver desde la ficha de un tema
 * devuelve la búsqueda —y el largo de la lista— tal como estaba.
 */
export default function Temas() {
  const navigate = useNavigate();
  // Ctrl/⌘ abre el tema en otra pestaña y deja el catálogo como está: comparar
  // varios temas es lo que uno viene a hacer acá.
  const ir = useIr();
  const abrirTema = (e: GestoDeApertura, id: number) => ir(e, `/temas/${id}`);
  const portalUrl = portalLoginUrl();

  const { leer, tandas, filtrar, verTandas } = useFiltrosUrl();
  const categoria = leer("area");
  const estado = leer("estado");
  const rutGuia = leer("guia");
  const guia: GuiaOpcion | null = rutGuia ? { rut: Number(rutGuia), nombre: leer("guia_nombre") } : null;

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useTemasInfinito({
    area: categoria ? Number(categoria) : "",
    estado,
    guia: guia?.rut ?? "",
    page_size: TANDA,
  });
  const cargadas = data?.pages.length ?? 0;
  const temas = useMemo(() => (data?.pages ?? []).flatMap((p) => p.results), [data]);
  const total = data?.pages[0]?.count ?? 0;

  // Pedir una tanda es subir la cuenta de la URL; traerla es este efecto. Así el
  // scroll y la vuelta desde una ficha (que llega con la cuenta ya subida)
  // recorren el mismo camino.
  useEffect(() => {
    if (cargadas > 0 && cargadas < tandas && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [cargadas, tandas, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const cargarMas = () => {
    if (hasNextPage && !isFetchingNextPage) verTandas(cargadas + 1);
  };
  const finDeLista = useFinALaVista<HTMLDivElement>(cargarMas, temas.length);

  // Las categorías son todas las áreas del sistema, no solo las de esta tanda:
  // el filtro pregunta a la base, así que puede traer temas que aquí no se ven.
  const { data: areas } = useAreas();
  const categorias: OpcionFiltro[] = useMemo(
    () => (areas ?? []).map((a) => ({ value: String(a.id), label: a.nombre })),
    [areas],
  );

  const { orden, onSort } = useOrden();

  // El orden de la tabla acomoda lo que se lleva cargado; el filtro lo hace el backend.
  const visibles = ordenar(temas, ACC_TEMA, orden);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 }, minHeight: "55vh" }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Catálogo de temas
      </Typography>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && <Alert severity="error">No se pudieron cargar los temas.</Alert>}

      {!isLoading && !isError && (
        <>
          <FiltrosTemas
            categorias={categorias}
            categoria={categoria}
            onCategoria={(v) => filtrar({ area: v })}
            estado={estado}
            onEstado={(v) => filtrar({ estado: v })}
            guia={guia}
            onGuia={(v) => filtrar({ guia: v ? String(v.rut) : "", guia_nombre: v?.nombre ?? "" })}
            total={total}
            disponibilidades={DISPONIBILIDAD_PUBLICA}
          />

          {total === 0 && (
            <Paper elevation={0} sx={{ p: 4, textAlign: "center", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
              <Typography color="text.secondary">No hay temas con esos filtros.</Typography>
            </Paper>
          )}

          {/* Desktop: tabla */}
          {visibles.length > 0 && (
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <TablaKit columnas={COLUMNAS} orden={orden} onSort={onSort} anchosFijos>
                <TableBody>
                  {visibles.map((t) => (
                    <FilaKit key={t.id} onClick={(e) => abrirTema(e, t.id)}>
                      <CeldaPrincipal>{t.titulo}</CeldaPrincipal>
                      <CeldaKit>
                        {/* Ancho fijo: el área larga se recorta en vez de invadir la columna vecina. */}
                        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ minWidth: 0 }}>
                          <Etiqueta truncar>{categoriaDe(t)}</Etiqueta>
                          {t.areas.length > 1 && <Etiqueta variante="contorno">+{t.areas.length - 1}</Etiqueta>}
                        </Stack>
                      </CeldaKit>
                      <CeldaKit>
                        <EstadoChip label={t.estado} tono={tonoDeTema(t.estado_codigo)} denso />
                      </CeldaKit>
                      <CeldaKit atenuada={!t.guia}>
                        <Guias tema={t} />
                      </CeldaKit>
                    </FilaKit>
                  ))}
                </TableBody>
              </TablaKit>
            </Box>
          )}

          {/* Móvil: tarjetas */}
          {visibles.length > 0 && (
            <Box sx={{ display: { xs: "block", sm: "none" } }}>
              <Stack spacing={1.5}>
                {visibles.map((t) => (
                  <TemaCard key={t.id} tema={t} onClick={(e) => abrirTema(e, t.id)} />
                ))}
              </Stack>
            </Box>
          )}

          {/* Final de la lista: lo que mira el observador para traer la tanda
              siguiente, con el botón para pedirla a mano. Se dice cuánto se lleva
              visto, que antes lo decía el aviso de recorte. */}
          {visibles.length > 0 && (
            <Box
              ref={finDeLista}
              sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, minHeight: 44, mt: 2.5 }}
            >
              {isFetchingNextPage ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  {/* Cuánto se lleva visto; en la primera tanda no hace falta decirlo. */}
                  {temas.length > TANDA && (
                    <Typography variant="caption" color="text.secondary">
                      {hasNextPage ? `${temas.length} de ${total} temas.` : `Esos son los ${total} temas.`}
                    </Typography>
                  )}
                  {hasNextPage && (
                    <Button variant="text" size="small" onClick={cargarMas}>
                      Cargar más temas
                    </Button>
                  )}
                </>
              )}
            </Box>
          )}

          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Para postular a un tema, inicia sesión.
            </Typography>
            <Button variant="contained" href={portalUrl ?? undefined} onClick={portalUrl ? undefined : () => navigate("/login")}>
              Iniciar sesión
            </Button>
          </Box>
        </>
      )}
    </Container>
  );
}

/** Guía y coguía en una sola columna: el guía arriba, el coguía debajo y tenue. */
function Guias({ tema }: { tema: Tema }) {
  return (
    <>
      {tema.guia || "—"}
      {tema.coguia && (
        <Typography variant="caption" color="text.secondary" display="block">
          Coguía: {tema.coguia}
        </Typography>
      )}
    </>
  );
}

function TemaCard({ tema, onClick }: { tema: Tema; onClick: (e: { ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean; button?: number }) => void }) {
  return (
    <Tarjeta onClick={onClick}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <Etiqueta>{categoriaDe(tema)}</Etiqueta>
        {tema.areas.length > 1 && <Etiqueta variante="contorno">+{tema.areas.length - 1}</Etiqueta>}
        <EstadoChip label={tema.estado} tono={tonoDeTema(tema.estado_codigo)} denso />
      </Stack>
      <TituloTarjeta>{tema.titulo}</TituloTarjeta>
      <Stack spacing={0.75}>
        <Field label="Guía" value={tema.guia || "—"} />
        {tema.coguia && <Field label="Coguía" value={tema.coguia} />}
      </Stack>
    </Tarjeta>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: "right" }}>
        {value}
      </Typography>
    </Stack>
  );
}
